import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Input } from '../Input';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { EmojiIcon } from '../EmojiIcon';
import CustomLink from '../CustomLink';
import { PRIVACY_POLICY_LINK } from '../../shared/links';
import { useSendResults } from '../../shared/use-send-results';
import { getMemoryKeeperPersonalization } from '../../shared/book-archetype-helpers';
import styles from './EmailSlideV8.css';

function EmailSlideV8({
    setEmail,
    onContinue,
    results,
    spriteVersion,
    legalLinks = { privacyPolicy: PRIVACY_POLICY_LINK }
}) {
    const emailRef = useRef(null);
    const afterSuccessfulSendResults = callback => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        callback(); /* eslint-disable-line */
        onContinue();
    };

    const { fields, changeField, isPending, submitForm, errors } = useSendResults({
        saveEmail: setEmail,
        afterCallback: afterSuccessfulSendResults,
        initialPrelander: results?.version,
        memoryKeeperPersonalization: getMemoryKeeperPersonalization(results.userResponses)
    });
    const isDidYouMean = Boolean(errors.form?.didYouMean);

    useEffect(() => {
        setTimeout(() => {
            emailRef.current?.focus();
        }, 50);
    }, []);

    return (
        <form
            className={styles.root}
            onSubmit={submitForm}
        >
            <h1 className={styles.title}>Enter your email to see your book details</h1>
            <ul className={styles.parts} aria-hidden='true'>
                {[['books', 'The\nThickness'], ['performing-arts', 'The\nGenre'], ['hot-pepper', 'The Heat']].map(([icon, title]) => (
                    <li key={icon} className={styles.part}>
                        <EmojiIcon name={icon} version={spriteVersion} />
                        <span className={styles.partTitle}>{title}</span>
                        <span className={styles.partHidden}>Hidden until the end</span>
                        <Icon name='checkItemFilled' className={styles.partCheck} />
                    </li>
                ))}
            </ul>
            <Input
                value={fields.email}
                name='email'
                onChange={changeField}
                className={styles.input}
                placeholder='Your email address'
                errorText={errors.email || (!isDidYouMean && errors.form?.message)}
                disabled={isPending}
                inputProps={{ autoComplete: 'email', type: 'email', inputMode: 'email' }}
                emailDomainSuggestions={{
                    list: ['gmail.com', 'yahoo.com', 'icloud.com', 'hotmail.com', 'aol.com', 'outlook.com', 'comcast.net', 'att.net', 'live.com', 'mail.com', 'msn.com']
                }}
                inputRef={emailRef}
                didYouMean={errors.form?.didYouMean}
            />
            <div className={styles.agreementBlock}>
                <Icon name='lockSolid' />
                <span>
                    {'We’ll use your email to save your plan and send you updates about Boundly. You can unsubscribe at any time. See our '}
                    <CustomLink
                        to={legalLinks.privacyPolicy}
                        target='_blank'
                    >
                        Privacy&nbsp;Policy
                    </CustomLink>
                    .
                </span>
            </div>
            <div className={styles.footer}>
                <div className={styles.footerWrapper}>
                    <Button
                        type='berry'
                        label='Continue'
                        isLoading={isPending}
                        inSlide
                        buttonProps={{ type: 'submit' }}
                    />
                </div>
            </div>
        </form>
    );
}

EmailSlideV8.propTypes = {
    setEmail: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    results: PropTypes.shape({ version: PropTypes.string, userResponses: PropTypes.array }).isRequired,
    spriteVersion: PropTypes.string.isRequired,
    legalLinks: PropTypes.object
};

export {
    EmailSlideV8
};
